// The savings calculator on /restaurants (#calculator): five fields with example values already
// in, results that update as you type, and "Contact us". The math is in src/restaurant-calc.ts.
// Contact us opens one email field; Send saves the lead with the numbers (see /api/restaurant-calc).
import { sendCalcLead } from '../api.ts';
import { brand } from '../../brand.ts';
import { BIG_NUMBER, CALC_CAPS, CONTACT, FOOTNOTE, SEND, SENT, CALC_DEFAULTS, CALC_KEYS, FINE_PRINT, calculate, capped, display, isBigNumber, showTablesLine, type CalcKey } from '../../restaurant-calc.ts';

const FIELDS: Record<CalcKey, { label: string; pre?: string; post?: string }> = {
  tables: { label: `Tables that split with ${brand.name} on a busy night` },
  saved: { label: `If ${brand.name} saves each table`, post: 'min' },
  turn: { label: 'Average turn time', post: 'min' },
  check: { label: 'Average check per table', pre: '$' },
  nights: { label: 'Busy nights a week' },
};

const UTM_KEYS = ['utm_source', 'utm_campaign', 'utm_content'];
const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

/** The UTM tags on the link the page loaded with. Read once, before any scrolling or edits. */
export function utmFromUrl(search = location.search): Record<string, string> {
  const q = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const k of UTM_KEYS) { const v = (q.get(k) || '').trim().slice(0, 100); if (v) out[k] = v; }
  return out;
}

const field = (k: CalcKey) => {
  const f = FIELDS[k];
  return `
    <label class="field c-field" for="c_${k}">
      <span class="c-lbl">${f.label}</span>
      <span class="c-in">${f.pre ? `<span class="c-unit" aria-hidden="true">${f.pre}</span>` : ''}<input id="c_${k}" name="${k}" class="input amt" type="text" inputmode="decimal" autocomplete="off" enterkeyhint="next" value="${CALC_DEFAULTS[k]}">${f.post ? `<span class="c-unit" aria-hidden="true">${f.post}</span>` : ''}</span>
    </label>`;
};

const row = (id: string, label: string, hi = false) =>
  `<tr${hi ? ' class="c-hi"' : ''}><th scope="row">${label}</th><td><b class="amt" id="${id}">–</b></td></tr>`;

export const calculatorSection = () => `
  <section class="card stack c-calc" id="calculator" aria-labelledby="calcTitle">
    <h2 class="h" id="calcTitle">Run your own numbers</h2>
    <p class="sub" style="margin:0">Example numbers to start. Change any of them.</p>
    <div class="c-fields">${CALC_KEYS.map(field).join('')}</div>
    <table class="c-results" aria-live="polite" aria-label="Extra revenue">
      <tbody>${row('r_night', 'Night')}${row('r_week', 'Week')}${row('r_month', 'Month')}${row('r_year', 'Year', true)}</tbody>
    </table>
    <p class="c-line" id="r_tables">About 3.3 more tables a night.</p>
    <p class="sub c-fine" id="r_fine" style="margin:0">${FINE_PRINT}</p>
    <form id="calcForm" class="stack" novalidate>
      <div class="stack" id="calcFields" hidden>
        <label class="field"><span class="c-lbl">Email</span><input id="cEmail" class="input" type="email" maxlength="120" autocomplete="email" inputmode="email" enterkeyhint="send" required></label>
      </div>
      <div class="error" id="cFormErr" role="alert" hidden></div>
      <button class="btn" id="calcSend" type="submit">${CONTACT}</button>
      <p class="sub" style="margin:0;font-size:12px">${FOOTNOTE}</p>
    </form>
  </section>`;

const el = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const val = (k: CalcKey) => (el<HTMLInputElement>('c_' + k)?.value ?? '');
const values = () => Object.fromEntries(CALC_KEYS.map((k) => [k, val(k)])) as Record<CalcKey, string>;

/** Puts the numbers on screen. Called on every keystroke. */
function render(): void {
  const c = calculate(values());
  const d = display(c);
  el('r_night').textContent = d.night;
  el('r_week').textContent = d.week;
  el('r_month').textContent = d.month;
  el('r_year').textContent = d.year;
  // The highlighter is for a total, so a dash doesn't get it.
  el('r_year').closest('tr')?.classList.toggle('c-hi', c.ok);
  const line = el('r_tables');
  // Hidden with the dashes, and when it would round to under 0.1 tables ("About 0 more tables").
  const shown = showTablesLine(c);
  line.textContent = shown ? `About ${d.tables} more ${d.tables === '1' ? 'table' : 'tables'} a night.` : '';
  line.hidden = !shown;
  // A big estimate swaps the fine print for a plainer warning.
  const big = isBigNumber(c);
  const fine = el('r_fine');
  fine.textContent = big ? BIG_NUMBER : FINE_PRINT;
  fine.classList.toggle('c-big', big);
}

/**
 * Wires the calculator. utm is what the page's link carried when it loaded. onSent runs once the
 * lead is saved, so the page can take away its other ways to send a second one.
 */
export function mountCalculator(utm: Record<string, string>, onSent: () => void = () => {}): void {
  render();
  const root = el('calculator');
  root.addEventListener('input', (e) => { if ((e.target as HTMLElement).id?.startsWith('c_')) render(); });
  // A number past its cap shows as the cap once they leave the field, so the math matches the screen.
  root.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement;
    const k = t.id?.startsWith('c_') ? (t.id.slice(2) as CalcKey) : null;
    if (!k || !(k in CALC_CAPS)) return;
    const n = capped(k, t.value);
    if (n !== null && String(n) !== t.value.trim()) { t.value = String(n); render(); }
  });
  let sending = false;
  el<HTMLFormElement>('calcForm').addEventListener('submit', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fields = el('calcFields');
    const err = el('cFormErr');
    const btn = el<HTMLButtonElement>('calcSend');
    if (fields.hidden) { fields.hidden = false; btn.textContent = SEND; el<HTMLInputElement>('cEmail').focus(); return; }
    if (sending) return;
    const email = el<HTMLInputElement>('cEmail').value.trim();
    if (!isEmail(email)) {
      err.textContent = "That email doesn't look right.";
      err.hidden = false;
      el<HTMLInputElement>('cEmail').focus();
      return;
    }
    err.hidden = true;
    sending = true;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      await sendCalcLead({ email, restaurant: '', name: '', optIn: false, inputs: values(), utm });
      el('calcForm').outerHTML = `<div class="c-thanks" role="status"><b>${SENT}</b></div>`;
      onSent();
    } catch (x) {
      err.textContent = (x as Error).message;
      err.hidden = false;
      btn.disabled = false;
      btn.textContent = SEND;
    } finally {
      sending = false;
    }
  });
}
